// types
import { CsvRow, FileToZip, JsonObject } from '../@types';

// papaparse
import Papa from 'papaparse';

// utils
import { truncate } from './strings';

// exceljs
import * as ExcelJS from 'exceljs';

// props
export interface CsvToArrProps {
	string: string;
	separator: string;
}

export const csvToArray = ({ string, separator }: CsvToArrProps) => {
	let array = string
		.trim()
		.split('\n')
		.filter((item) => {
			// filter out empty lines
			const itemWithoutCommas = item.trim().replaceAll(',', '');
			return itemWithoutCommas !== '';
		})
		.map((item) => item.split(separator));

	array = array.map((a) => {
		return a.map((b) => {
			return b.trim();
		});
	});

	return array;
};

export interface DataUrlToFileParams {
	dataUrl: string;
	fileName: string;
}

// https://stackoverflow.com/a/38935990/2262604
export const dataUrlToFile = ({ dataUrl, fileName }: DataUrlToFileParams) => {
	const arr = dataUrl.split(',');
	const mime = arr[0].match(/:(.*?);/)?.[1];
	const bstr = window.atob(arr[arr.length - 1]);
	let n = bstr.length;
	const u8arr = new Uint8Array(n);

	while (n--) {
		u8arr[n] = bstr.charCodeAt(n);
	}

	return new File([u8arr], fileName, { type: mime });
};

export const escapeCsvValue = (value: string | number | null | undefined): string => {
	const strValue = String(value || '');
	// If value contains comma, newline, or double quote, wrap in quotes and escape quotes
	if (strValue.includes(',') || strValue.includes('\n') || strValue.includes('"')) {
		return `"${strValue.replaceAll('"', '""')}"`;
	}
	return strValue;
};

export interface JsonToCsvParams {
	array: JsonObject[];
}

export const jsonToCsv = ({ array }: JsonToCsvParams) => {
	const keys: string[] = Object.keys(array[0]);

	const csv = [
		keys.join(','),
		...array.map((row) =>
			keys
				.map((key) => {
					const value = row[key];
					if (value === undefined || value === null) {
						return '';
					}
					return escapeCsvValue(value as string);
				})
				.join(',')
		),
	].join('\r\n');

	return csv;
};

export interface ParseCsvProps {
	string: string;
	separator?: string;
}

export interface ParseExcelProps {
	file: File;
	sheetName?: string;
}

export interface ExcelSheetInfo {
	name: string;
	rowCount: number;
}

export const parseCsv = ({ string, separator = '' }: ParseCsvProps) => {
	// Parse CSV and auto-detect delimiters, trim values, and skip empty lines
	const parsed = Papa.parse(string, {
		delimiter: separator,
		header: false,
		skipEmptyLines: true,
		quoteChar: '"',
		escapeChar: '"',
		transform: (value: string) => value.trim(),
	});

	if (parsed.errors.length > 0) {
		throw new Error('CSV parsing failed: ' + parsed.errors.map((e) => e.message).join(', '));
	}

	const data = parsed.data as string[][];

	if (data.length === 0) {
		return { rows: [], headers: [] };
	}

	const headers = data[0] || [];
	const rows: CsvRow[] = [];

	// Process data rows (skip header row)
	for (let i = 1; i < data.length; i++) {
		const rowArray = data[i] || [];
		const row: CsvRow = {};

		// Map values to headers, skipping empty header columns
		headers.forEach((header, index) => {
			if (header) {
				row[header] = rowArray[index] || '';
			}
		});

		rows.push(row);
	}

	// Filter out empty headers
	const headersFiltered = headers.filter((h) => h !== '' && h !== undefined);

	return { rows, headers: headersFiltered };
};

export const parseExcel = async ({
	file,
	sheetName,
}: ParseExcelProps): Promise<{
	rows: CsvRow[];
	headers: string[];
	sheets: ExcelSheetInfo[];
}> => {
	const arrayBuffer = await file.arrayBuffer();
	const workbook = new ExcelJS.Workbook();
	await workbook.xlsx.load(arrayBuffer);

	const sheets: ExcelSheetInfo[] = workbook.worksheets.map((ws) => ({
		name: ws.name,
		rowCount: ws.rowCount,
	}));

	const targetSheetName = sheetName || sheets[0]?.name;
	const worksheet = workbook.getWorksheet(targetSheetName);

	if (!worksheet) {
		return { rows: [], headers: [], sheets };
	}

	// Convert any ExcelJS cell value type to a plain string
	const getCellString = (cell: ExcelJS.Cell): string => {
		const value = cell.value;
		if (value === null || value === undefined) return '';
		// Formula: use the cached result
		if (typeof value === 'object' && 'formula' in value) {
			const result = (value as ExcelJS.CellFormulaValue).result;
			if (result === null || result === undefined) return '';
			if (typeof result === 'object' && 'error' in result) return '';
			return String(result);
		}
		// Shared formula: same as formula
		if (typeof value === 'object' && 'sharedFormula' in value) {
			const result = (value as ExcelJS.CellSharedFormulaValue).result;
			if (result === null || result === undefined) return '';
			if (typeof result === 'object' && 'error' in result) return '';
			return String(result);
		}
		// Rich text: concatenate all text runs
		if (typeof value === 'object' && 'richText' in value) {
			return (value as ExcelJS.CellRichTextValue).richText.map((rt) => rt.text).join('');
		}
		// Hyperlink: use the display text
		if (typeof value === 'object' && 'text' in value) {
			return String((value as ExcelJS.CellHyperlinkValue).text || '');
		}
		return String(value);
	};

	const headers: string[] = [];
	const rows: CsvRow[] = [];

	let headerRowNumber = -1;

	// for each non-empty row
	worksheet.eachRow({ includeEmpty: false }, (dataRow, rowNumber) => {
		if (headerRowNumber === -1) {
			// Define header row number as the first non-empty row
			headerRowNumber = rowNumber;

			// Map header values
			dataRow.eachCell({ includeEmpty: true }, (cell, colNumber) => {
				headers[colNumber - 1] = getCellString(cell);
			});
		} else {
			const row: CsvRow = {};

			// Map cell values to headers
			dataRow.eachCell({ includeEmpty: true }, (cell, colNumber) => {
				const header = headers[colNumber - 1];
				if (header) {
					row[header] = getCellString(cell);
				}
			});
			rows.push(row);
		}
	});

	// Filter out empty headers
	const headersFiltered = headers.filter((h) => h !== '' && h !== undefined);

	return { rows, headers: headersFiltered, sheets };
};

export const getFileExtension = (filename: string): string => {
	return filename.split('.').pop()?.toLowerCase() || '';
};

export const isExcelFile = (filename: string): boolean => {
	const extension = getFileExtension(filename);
	return ['xlsx', 'xls'].includes(extension);
};

export const isCsvFile = (filename: string): boolean => {
	const extension = getFileExtension(filename);
	return extension === 'csv';
};

export const readCsvFile = async (file: File): Promise<string> => {
	const buffer = await file.arrayBuffer();
	// Try decoding UTF-8 and throw error if file contains invalid UTF-8 bytes
	try {
		return new TextDecoder('utf-8', { fatal: true }).decode(buffer);
	} catch {
		// Decode using windows-1252 (covers ISO-8859-1 and common European encodings)
		return new TextDecoder('windows-1252').decode(buffer);
	}
};

export interface DownloadFileParams {
	data: BlobPart;
	name?: string;
	type: BlobPropertyBag['type'];
}

export const downloadFile = ({ data, name, type }: DownloadFileParams) => {
	// create anchor
	const a = document.createElement('a');
	const blob = new Blob([data], { type });
	const url = window.URL.createObjectURL(blob);

	// set anchor attributes
	a.setAttribute('href', url);

	if (name) {
		const nameSanitized = sanitizeFilename(name);
		a.setAttribute('download', nameSanitized);
	}

	// append anchor
	document.body.appendChild(a);

	// click anchor
	a.click();

	// remove anchor
	window.URL.revokeObjectURL(url);
	a.remove();

	return {
		blob,
		url,
	};
};

/**
 * Replaces characters in strings that are illegal/unsafe for filenames.
 * Unsafe characters are either removed or replaced by a substitute set
 * in the optional `replacement` string.
 *
 * Illegal Characters on Various Operating Systems
 * / ? < > \ : * | "
 * https://kb.acronis.com/content/39790
 *
 * Unicode Control codes
 * C0 0x00-0x1f & C1 (0x80-0x9f)
 * http://en.wikipedia.org/wiki/C0_and_C1_control_codes
 *
 * Reserved filenames on Unix-based systems (".", "..")
 * Reserved filenames in Windows ("CON", "PRN", "AUX", "NUL", "COM1",
 * "COM2", "COM3", "COM4", "COM5", "COM6", "COM7", "COM8", "COM9",
 * "LPT1", "LPT2", "LPT3", "LPT4", "LPT5", "LPT6", "LPT7", "LPT8", and
 * "LPT9") case-insesitively and with or without filename extensions.
 *
 * Capped at 255 characters in length.
 * http://unix.stackexchange.com/questions/32795/what-is-the-maximum-allowed-filename-and-folder-size-with-ecryptfs
 *
 * @param  {String} input   Original filename
 * @param  {String} replacement Replacement for illegal characters
 * @return {String}         Sanitized filename
 */
export const sanitizeFilename = (input: string, replacement = ''): string => {
	const illegalRe = /[/?<>\\:*|"]/g;
	// eslint-disable-next-line no-control-regex
	const controlRe = /[\x00-\x1F\x80-\x9F]/g;
	const reservedRe = /^\.+$/;
	const windowsReservedRe = /^(con|prn|aux|nul|com[0-9]|lpt[0-9])(\..*)?$/i;
	const windowsTrailingRe = /[. ]+$/;

	if (typeof input !== 'string') {
		throw new Error('Input must be string');
	}
	const sanitized = input
		.replace(illegalRe, replacement)
		.replace(controlRe, replacement)
		.replace(reservedRe, replacement)
		.replace(windowsReservedRe, replacement)
		.replace(windowsTrailingRe, replacement);

	return truncate({ str: sanitized, end: 255 });
};

export interface DownloadZipParams {
	files: FileToZip[];
	zipFilename: string;
}

export const downloadZip = async ({ files, zipFilename }: DownloadZipParams) => {
	// Dynamic import of JSZip to reduce bundle size
	const JSZip = (await import('jszip')).default;

	const zip = new JSZip();

	// Add each file to the zip
	files.forEach(({ filename, content }) => {
		zip.file(filename, content);
	});

	// Generate the zip file
	const zipBlob = await zip.generateAsync({ type: 'blob' });

	// Download the zip file
	downloadFile({
		data: zipBlob,
		name: zipFilename,
		type: 'application/zip',
	});
};
